import { DEFAULT_PIN_ICON, VIDEO_UPLOAD_PIN_ICON } from '@repo/store/maps/utils/mapData';
import type { MapData } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, getIconForGlyph } from '@repo/types/maps/map-utils';
import { EVENT_CATEGORY_ICONS } from '@repo/utility/events';
import { create as createFont, type Font } from 'fontkit';

interface FontConfig {
    apiName: string;
    prefix: string;
    dynamic?: boolean;
}

const FONT_API_URL = process.env.NEXT_PUBLIC_FONT_URL || 'https://fonts.wemod.com';

const BUILT_IN_FONTS: Record<string, FontConfig> = {
    MaterialSymbolsRounded: { apiName: 'material-symbols-rounded', prefix: 'material:' },
    Roundies: { apiName: 'roundies', prefix: 'roundies:' },
    FlatIconSolidRounded: { apiName: 'uicons-solid-rounded', prefix: '' },
};

export const BUILT_IN_FONT_NAMES = Object.keys(BUILT_IN_FONTS);

function getFontConfig(fontName: string): FontConfig {
    const builtInFont = BUILT_IN_FONTS[fontName];
    if (builtInFont) {
        return builtInFont;
    }

    return { apiName: fontName, prefix: `${fontName}:`, dynamic: true };
}

function createFontUrl(fontName: string): URL {
    const fontConfig = getFontConfig(fontName);
    const url = new URL(FONT_API_URL);
    url.searchParams.set('font', fontConfig.apiName);
    return url;
}

export function getGameFontDescriptorUrl(fontId: string): string {
    const url = createFontUrl(fontId);
    url.searchParams.set('format', 'map-icons');
    return url.toString();
}

export function getGameFontUrl(fontId: string): string {
    return createFontUrl(fontId).toString();
}

export function getGameFontDownloadUrl(fontId: string): string {
    const url = createFontUrl(fontId);
    url.searchParams.set('download', 'true');
    return url.toString();
}

function normalizeGlyphNames(fontName: string, glyphNames: string[]): string[] {
    const fontConfig = getFontConfig(fontName);

    const names = glyphNames
        .map(name =>
            fontConfig.prefix && name.startsWith(fontConfig.prefix) ? name.slice(fontConfig.prefix.length) : name,
        )
        .filter(name => name.trim().length > 0);

    return fontName === 'FlatIconSolidRounded' ? names.map(name => `fi-sr-${name}`) : names;
}

function groupIconsByFont(icons: Iterable<string>): Record<string, string[]> {
    const fontGlyphs: Record<string, Set<string>> = {};
    const addIcon = (icon: string) => {
        const fontName = getFontInfoForGlyph(icon).fontName;
        const glyphs = fontGlyphs[fontName] ?? new Set<string>();
        glyphs.add(icon);
        fontGlyphs[fontName] = glyphs;
    };

    for (const icon of icons) addIcon(icon);
    return Object.fromEntries(Object.entries(fontGlyphs).map(([fontName, glyphs]) => [fontName, [...glyphs]]));
}

function extractFontsFromMapData(mapData: MapData | null): Record<string, string[]> {
    if (!mapData) return {};

    const icons = [DEFAULT_PIN_ICON, VIDEO_UPLOAD_PIN_ICON];
    for (const subcategory of mapData.metadata.subcategories) {
        if (subcategory.icon) icons.push(subcategory.icon);
    }

    return groupIconsByFont(icons);
}

function generateFontSubsetUrls(fontGlyphs: Record<string, string[]>): Record<string, string> {
    const urls: Record<string, string> = {};

    for (const [fontName, glyphNames] of Object.entries(fontGlyphs)) {
        if (getFontConfig(fontName).dynamic) {
            urls[fontName] = createFontUrl(fontName).toString();
            continue;
        }

        const validGlyphNames = normalizeGlyphNames(fontName, glyphNames).sort();
        const url = createFontUrl(fontName);
        if (validGlyphNames.length === 0) continue;

        url.searchParams.set('glyphs', validGlyphNames.join(','));
        urls[fontName] = url.toString();
    }

    return urls;
}

export function getBuiltInFontUrls(): Record<string, string> {
    return Object.fromEntries(BUILT_IN_FONT_NAMES.map(fontName => [fontName, createFontUrl(fontName).toString()]));
}

export function getFontStyleUrls(
    fontUrls: Record<string, string>,
    useFullBuiltInFonts: boolean,
): Record<string, string> {
    return useFullBuiltInFonts ? { ...fontUrls, ...getBuiltInFontUrls() } : fontUrls;
}

export function getMapFontUrls(mapData: MapData | null): Record<string, string> {
    const fonts = extractFontsFromMapData(mapData);
    return generateFontSubsetUrls(fonts);
}

const EVENT_ICON_GLYPHS = [...new Set(Object.values(EVENT_CATEGORY_ICONS))];
let eventIconFontPromise: Promise<Font> | null = null;
const eventIconSvgCache = new Map<string, string>();

function loadEventIconFont(): Promise<Font> {
    if (!eventIconFontPromise) {
        eventIconFontPromise = (async () => {
            const url = new URL(FONT_API_URL);
            url.searchParams.set('font', 'material-symbols-rounded');
            url.searchParams.set('glyphs', EVENT_ICON_GLYPHS.join(','));

            const response = await fetch(url.toString());
            if (!response.ok) throw new Error(`Font fetch failed: ${response.status}`);
            const buffer = await response.arrayBuffer();
            return createFont(Buffer.from(buffer)) as Font;
        })();
        eventIconFontPromise.catch(() => {
            eventIconFontPromise = null;
        });
    }
    return eventIconFontPromise;
}

export async function getEventIconSvg(iconName: string, size: number): Promise<string> {
    const cacheKey = `${iconName}-${size}`;
    if (eventIconSvgCache.has(cacheKey)) {
        // biome-ignore lint/style/noNonNullAssertion: FIXME: unknown intent
        return eventIconSvgCache.get(cacheKey)!;
    }

    const font = await loadEventIconFont();
    const unicodeChar = getIconForGlyph(`material:${iconName}`);
    const run = font.layout(unicodeChar);
    const glyphPath = run?.glyphs?.[0]?.path?.toSVG?.() ?? '';
    const bbox = run?.glyphs?.[0]?.path?.bbox ?? { minX: 0, minY: 0, width: 0, height: 0 };

    if (!glyphPath || bbox.width === 0 || bbox.height === 0) {
        return '';
    }

    const scaleX = size / bbox.width;
    const scaleY = size / bbox.height;
    const scale = Math.min(scaleX, scaleY);
    const centerX = bbox.minX + bbox.width / 2;
    const centerY = bbox.minY + bbox.height / 2;
    const translateX = size / 2 - centerX * scale;
    const translateY = size / 2 + centerY * scale;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <g transform="translate(${translateX}, ${translateY}) scale(${scale}, ${-scale})">
            <path d="${glyphPath}" fill="#FFFFFF"/>
        </g>
    </svg>`;

    eventIconSvgCache.set(cacheKey, svg);
    return svg;
}
